import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import QrCode2Icon from '@material-ui/icons/CropFree';
import SearchIcon from '@material-ui/icons/Search';
import WifiTetheringIcon from '@material-ui/icons/WifiTethering';
import { useSnackbar } from 'notistack';

import { ConfirmationDialog } from '../../../../components/ConfirmationDialog';
import { ICodesConflict, IProduct, useProduct } from '../../hooks/product';

// HEX do leitor RFID (duas seções '2E' = os pontos de TP.xx.yyy) vira o
// DEC legível — mesma regra da conferência na API; um EAN numérico, que
// também é hex válido, nunca converte por engano.
function decodificarHex(valor: string): string {
  const hex = valor.trim();
  if (hex.length < 4 || hex.length % 2 !== 0) return valor;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return valor;
  if (hex.toUpperCase().split('2E').length !== 3) return valor;
  let texto = '';
  for (let i = 0; i < hex.length; i += 2) {
    const byte = parseInt(hex.slice(i, i + 2), 16);
    if (byte < 32 || byte > 126) return valor;
    texto += String.fromCharCode(byte);
  }
  return texto;
}

interface ICodesDialogProps {
  open: boolean;
  product: IProduct | null;
  onClose(): void;
  onSaved(productId: number, codes: string[]): void;
}

export const CodesDialog = ({
  open,
  product,
  onClose,
  onSaved,
}: ICodesDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { updateProductCodes } = useProduct();

  const [codes, setCodes] = useState<string[]>([]);
  const [original, setOriginal] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [newCodes, setNewCodes] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<ICodesConflict[] | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (open && product) {
      const initial = (product.codes || []).map((code) => `${code}`.trim());
      setCodes(initial);
      setOriginal(initial);
      setFilter('');
      setNewCodes('');
      setEditingIndex(null);
      setConflicts(null);
    }
  }, [open, product]);

  const dirty = useMemo(
    () => JSON.stringify(codes) !== JSON.stringify(original),
    [codes, original]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toUpperCase();
    return codes
      .map((code, index) => ({ code, index }))
      .filter((item) => !needle || item.code.toUpperCase().includes(needle));
  }, [codes, filter]);

  const handleAdd = useCallback(() => {
    const entries = newCodes
      .split(/[\n,;]+/)
      .map((entry) => decodificarHex(entry.trim()).toUpperCase())
      .filter((entry) => entry.length > 0);
    if (entries.length === 0) return;

    const existing = new Set(codes.map((code) => code.toUpperCase()));
    const added: string[] = [];
    let duplicated = 0;
    for (const entry of entries) {
      if (existing.has(entry)) {
        duplicated += 1;
        continue;
      }
      existing.add(entry);
      added.push(entry);
    }

    if (added.length > 0) setCodes((prev) => [...prev, ...added]);
    setNewCodes('');
    if (duplicated > 0) {
      enqueueSnackbar(
        `${duplicated} código(s) já estavam na lista e foram ignorados.`,
        { variant: 'warning' }
      );
    }
  }, [codes, enqueueSnackbar, newCodes]);

  const handleStartEdit = useCallback((index: number, value: string) => {
    setEditingIndex(index);
    setEditingValue(value);
  }, []);

  const handleConfirmEdit = useCallback(() => {
    if (editingIndex === null) return;
    const value = decodificarHex(editingValue.trim()).toUpperCase();
    if (!value) return;
    const clash = codes.some(
      (code, index) => index !== editingIndex && code.toUpperCase() === value
    );
    if (clash) {
      enqueueSnackbar('Este código já está na lista.', { variant: 'warning' });
      return;
    }
    setCodes((prev) =>
      prev.map((code, index) => (index === editingIndex ? value : code))
    );
    setEditingIndex(null);
  }, [codes, editingIndex, editingValue, enqueueSnackbar]);

  const handleDelete = useCallback((index: number) => {
    setCodes((prev) => prev.filter((_, i) => i !== index));
    setEditingIndex(null);
  }, []);

  const handleSave = useCallback(
    async (force = false) => {
      if (!product) return;
      setSaving(true);
      try {
        const response = await updateProductCodes(product.id, codes, force);
        if (response.conflicts && response.conflicts.length > 0) {
          setConflicts(response.conflicts);
          return;
        }
        if (response.error) {
          enqueueSnackbar(`Não foi possível salvar: ${response.error}`, {
            variant: 'error',
          });
          return;
        }
        enqueueSnackbar('Códigos salvos.', { variant: 'success' });
        onSaved(product.id, response.codes || codes);
        onClose();
      } catch (error) {
        enqueueSnackbar('Não foi possível salvar os códigos.', {
          variant: 'error',
        });
      } finally {
        setSaving(false);
        setConflicts(null);
      }
    },
    [codes, enqueueSnackbar, onClose, onSaved, product, updateProductCodes]
  );

  const handleRequestClose = useCallback(() => {
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  if (!product) return null;

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} fullWidth maxWidth="sm">
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="center" gridGap={8}>
            <WifiTetheringIcon color="primary" />
            <Typography variant="h6">Códigos RFID</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {product.description} · SKU {product.sku}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="body2" color="textSecondary">
              {codes.length} código{codes.length === 1 ? '' : 's'}
              {dirty ? ' · alterações não salvas' : ''}
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Filtrar código..."
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            border={1}
            borderColor="grey.300"
            borderRadius={8}
            maxHeight={280}
            overflow="auto"
          >
            {visible.length === 0 ? (
              <Box p={2}>
                <Typography variant="body2" color="textSecondary" align="center">
                  {codes.length === 0
                    ? 'Nenhum código cadastrado — adicione abaixo.'
                    : 'Nenhum código bate com o filtro.'}
                </Typography>
              </Box>
            ) : (
              visible.map(({ code, index }) => (
                <Box
                  key={`${code}-${index}`}
                  display="flex"
                  alignItems="center"
                  gridGap={8}
                  px={1.5}
                  py={0.25}
                  style={{ borderBottom: '1px solid #eef1f2' }}
                >
                  <QrCode2Icon fontSize="small" color="disabled" />
                  {editingIndex === index ? (
                    <>
                      <TextField
                        size="small"
                        value={editingValue}
                        onChange={(event) => setEditingValue(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleConfirmEdit();
                          if (event.key === 'Escape') setEditingIndex(null);
                        }}
                        autoFocus
                        fullWidth
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                      />
                      <Tooltip title="Confirmar">
                        <IconButton size="small" onClick={handleConfirmEdit}>
                          <CheckIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => setEditingIndex(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        style={{ flex: 1, fontFamily: 'monospace' }}
                      >
                        {code}
                      </Typography>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(index, code)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              ))
            )}
          </Box>

          <Box display="flex" gridGap={8} mt={2} alignItems="flex-start">
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              multiline
              rowsMax={4}
              placeholder="Adicionar código(s) — DEC ou HEX, um por linha..."
              value={newCodes}
              onChange={(event) => setNewCodes(event.target.value)}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!newCodes.trim()}
            >
              Adicionar
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            component="div"
            style={{ marginTop: 6 }}
          >
            HEX do leitor (ex.: 54502E30312E303034303330) vira o DEC sozinho.
            Duplicado no mesmo produto é ignorado; código de outro SKU é
            avisado antes de salvar.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleRequestClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave(false)}
            disabled={!dirty || saving || editingIndex !== null}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={conflicts !== null}
        title="Código já usado em outro SKU"
        description={
          conflicts
            ? `${conflicts
                .slice(0, 5)
                .map(
                  (conflict) =>
                    `${conflict.code} já está em ${conflict.sku} (${conflict.description})`
                )
                .join('; ')}${
                conflicts.length > 5 ? ` e mais ${conflicts.length - 5}...` : ''
              }. Salvar mesmo assim?`
            : ''
        }
        onSubmit={() => handleSave(true)}
        onClose={() => setConflicts(null)}
      />

      <ConfirmationDialog
        open={confirmClose}
        title="Descartar alterações?"
        description="Há alterações não salvas nos códigos. Deseja descartá-las?"
        onSubmit={() => {
          setConfirmClose(false);
          onClose();
        }}
        onClose={() => setConfirmClose(false)}
      />
    </>
  );
};
